import React, { useState } from 'react';
import { Navbar, Nav, Container, NavDropdown, Button, Offcanvas, Accordion } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ARTICLE_MENU_GROUPS } from '../data/articlesNav';
import { LocalizedLink } from './LocalizedLink';
import { useLocale } from '../contexts/LocaleContext';
import { localizePath } from '../utils/localePaths';
import { pathnameWithoutLocale } from '../utils/seoLocale';

function AnchorSectionLink({ id, children, onNavigate }) {
  const location = useLocation();
  const { localePrefix } = useLocale();
  const homePath = localizePath('/', localePrefix);
  const isHome = location.pathname === homePath || location.pathname === `${homePath}/`;
  const hashUrl = `${homePath}#${id}`;

  if (isHome) {
    return (
      <Nav.Link href={`#${id}`} onClick={onNavigate}>
        {children}
      </Nav.Link>
    );
  }

  return (
    <Nav.Link as={Link} to={hashUrl} onClick={onNavigate}>
      {children}
    </Nav.Link>
  );
}

function UseCasesMobileAccordion({ location, localePrefix, t, onNavigate }) {
  return (
    <Accordion flush className="navbar-use-cases-accordion w-100">
      <Accordion.Item eventKey="use-cases">
        <Accordion.Header>{t('nav.useCases')}</Accordion.Header>
        <Accordion.Body className="p-0 pt-1">
          <Accordion flush className="navbar-use-cases-groups">
            {ARTICLE_MENU_GROUPS.map((group) => (
              <Accordion.Item eventKey={group.groupKey} key={group.groupKey}>
                <Accordion.Header>{t(`nav.groups.${group.groupKey}`)}</Accordion.Header>
                <Accordion.Body className="p-0">
                  <Nav className="flex-column">
                    {group.items.map((item) => (
                      <Nav.Link
                        key={item.to}
                        as={LocalizedLink}
                        to={item.to}
                        active={location.pathname === localizePath(item.to, localePrefix)}
                        onClick={onNavigate}
                        className="navbar-use-cases-link"
                      >
                        {t(`articles.${item.articleKey}`)}
                      </Nav.Link>
                    ))}
                  </Nav>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

function UseCasesNavDropdown({ location, localePrefix, t, onNavigate }) {
  return (
    <NavDropdown title={t('nav.useCases')} id="public-cas-d-usage-dropdown" align="end">
      {ARTICLE_MENU_GROUPS.map((group, gi) => (
        <React.Fragment key={group.groupKey}>
          <NavDropdown.Header>{t(`nav.groups.${group.groupKey}`)}</NavDropdown.Header>
          {group.items.map((item) => (
            <NavDropdown.Item
              key={item.to}
              as={LocalizedLink}
              to={item.to}
              active={location.pathname === localizePath(item.to, localePrefix)}
              onClick={onNavigate}
            >
              {t(`articles.${item.articleKey}`)}
            </NavDropdown.Item>
          ))}
          {gi < ARTICLE_MENU_GROUPS.length - 1 ? <NavDropdown.Divider /> : null}
        </React.Fragment>
      ))}
    </NavDropdown>
  );
}

function PublicNavLinks({ location, locale, localePrefix, t, onNavigate, vertical = false }) {
  const pathNoLocale = pathnameWithoutLocale(location.pathname);
  const frTarget = pathNoLocale === '/' ? '/' : pathNoLocale;
  const enTarget = pathNoLocale === '/' ? '/en' : `/en${pathNoLocale}`;
  const navClass = vertical ? 'flex-column w-100' : 'align-items-lg-center gap-lg-1 ms-auto flex-lg-row';

  return (
    <Nav className={navClass}>
      <AnchorSectionLink id="advantages" onNavigate={onNavigate}>
        {t('nav.advantages')}
      </AnchorSectionLink>
      <AnchorSectionLink id="how-it-works" onNavigate={onNavigate}>
        {t('nav.howItWorks')}
      </AnchorSectionLink>
      <Nav.Link as={LocalizedLink} to="/tarifs/" onClick={onNavigate}>
        {t('nav.pricing')}
      </Nav.Link>
      {vertical ? (
        <UseCasesMobileAccordion
          location={location}
          localePrefix={localePrefix}
          t={t}
          onNavigate={onNavigate}
        />
      ) : (
        <UseCasesNavDropdown
          location={location}
          localePrefix={localePrefix}
          t={t}
          onNavigate={onNavigate}
        />
      )}
      <Nav.Link as={LocalizedLink} to="/login/" className={vertical ? '' : 'ms-lg-2'} onClick={onNavigate}>
        {t('nav.login')}
      </Nav.Link>
      <NavDropdown
        title={locale === 'en' ? 'EN' : 'FR'}
        id="public-lang-dropdown"
        align={vertical ? 'start' : 'end'}
        className={vertical ? '' : 'ms-lg-1'}
      >
        <NavDropdown.Item as={Link} to={frTarget} active={locale === 'fr'} onClick={onNavigate}>
          Français
        </NavDropdown.Item>
        <NavDropdown.Item as={Link} to={enTarget} active={locale === 'en'} onClick={onNavigate}>
          English
        </NavDropdown.Item>
      </NavDropdown>
    </Nav>
  );
}

export default function PublicSiteNavbar() {
  const location = useLocation();
  const { t } = useTranslation('common');
  const { locale, localePrefix } = useLocale();
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  const closeOffcanvas = () => setShowOffcanvas(false);

  return (
    <>
      <Navbar expand="lg" className="navbar-frosted sticky-top">
        <Container className="d-flex flex-wrap align-items-center">
          <Navbar.Brand as={LocalizedLink} to="/" className="d-flex align-items-center me-auto">
            <i className="bi bi-camera-video-fill text-primary me-2 fs-4" />
            <span className="fw-bold">{t('brand')}</span>
          </Navbar.Brand>

          <div className="d-none d-lg-flex align-items-center gap-2 ms-auto">
            <PublicNavLinks
              location={location}
              locale={locale}
              localePrefix={localePrefix}
              t={t}
            />
            <Button as={LocalizedLink} to="/register/" variant="primary" className="px-4 rounded-pill ms-2">
              <i className="bi bi-person-plus me-2" />
              {t('nav.register')}
            </Button>
          </div>

          <button
            type="button"
            className="navbar-toggler navbar-toggler-touch d-lg-none ms-2"
            aria-controls="public-site-offcanvas"
            aria-expanded={showOffcanvas}
            aria-label={t('nav.menuToggle', { defaultValue: 'Menu' })}
            onClick={() => setShowOffcanvas(true)}
          >
            <span className="navbar-toggler-icon" />
          </button>
        </Container>
      </Navbar>

      <Offcanvas
        show={showOffcanvas}
        onHide={closeOffcanvas}
        placement="end"
        className="navbar-offcanvas d-lg-none"
        id="public-site-offcanvas"
        aria-labelledby="public-site-offcanvas-label"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="public-site-offcanvas-label">{t('brand')}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column">
          <PublicNavLinks
            location={location}
            locale={locale}
            localePrefix={localePrefix}
            t={t}
            onNavigate={closeOffcanvas}
            vertical
          />
          <div className="border-top pt-3 mt-auto">
            <Button
              as={LocalizedLink}
              to="/register/"
              variant="primary"
              className="w-100 rounded-pill"
              onClick={closeOffcanvas}
            >
              <i className="bi bi-person-plus me-2" />
              {t('nav.register')}
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
